import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe, UserCircle, KeyRound, Camera, TriangleAlert, ShieldCheck, RefreshCw } from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { GlassCard, PageHeader, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { useAuth } from "../context/AuthContext";

const CLOUDFLARE_STEPS = [
  "Crie uma conta grátis na Cloudflare (cloudflare.com) e clique em \"Add site\" indicando o seu domínio — pode escolher o plano Free.",
  "No site onde comprou o domínio (GoDaddy, Namecheap, Hostinger...), mude os nameservers para os dois que a Cloudflare indicar.",
  "Na Cloudflare, vá a DNS → Records → Add record: tipo CNAME, nome @ (ou \"painel\"), destino: cinebot.site.",
  "Ative o proxy: a nuvem do registo tem de ficar LARANJA (Proxied).",
  "Em SSL/TLS, selecione o modo \"Full\".",
  "Volte aqui, guarde o domínio no campo abaixo e está feito — os seus links M3U passam a usar o seu domínio de imediato.",
];

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [name, setName] = useState(user.name || "");
  const [savingName, setSavingName] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarStamp, setAvatarStamp] = useState(0);
  const isOwner = user.role === "super_admin";
  const [dedupStatus, setDedupStatus] = useState(null);
  const [dedupBusy, setDedupBusy] = useState("");
  const loadDedup = async () => { try { const { data } = await api.get("/dedup/status"); setDedupStatus(data); } catch { /* silêncio */ } };
  useEffect(() => { loadDedup(); }, []); // eslint-disable-line
  useEffect(() => {
    if (dedupStatus?.job?.status !== "running") return undefined;
    const t = setInterval(loadDedup, 5000);
    return () => clearInterval(t);
  }, [dedupStatus?.job?.status]); // eslint-disable-line
  const runDedup = async (kind) => {
    setDedupBusy(kind);
    try {
      const { data } = await api.post(`/dedup/${kind}`);
      if (data.nothing) toast.info("Nada a processar por agora");
      else toast.success(kind === "backfill" ? `A calcular fingerprints de ${data.job?.total ?? ""} ficheiros em background…` : "A reclamar espaço duplicado em background…");
      loadDedup();
    } catch (e) { toast.error(formatApiError(e)); } finally { setDedupBusy(""); }
  };
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [domain, setDomain] = useState(user.custom_domain || "");
  const [savingDomain, setSavingDomain] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [sessionHours, setSessionHours] = useState(user.session_hours ?? 24);
  const [savingSession, setSavingSession] = useState(false);
  const [mfaStep, setMfaStep] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPw, setMfaPw] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const avatarUrl = user.avatar_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/auth/avatar/${user.id}?v=${avatarStamp || 1}`
    : null;

  const saveName = async () => {
    if (!name.trim()) return;
    setSavingName(true);
    try {
      await api.put("/auth/profile", { name: name.trim() });
      setUser({ ...user, name: name.trim() });
      toast.success("Nome atualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingName(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/avatar", fd);
      setUser({ ...user, avatar_path: data.avatar_path });
      setAvatarStamp(Date.now());
      toast.success("Foto de perfil atualizada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const changePassword = async () => {
    if (newPw !== confirmPw) {
      toast.error("A confirmação não bate com a nova senha");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw, confirm_password: confirmPw });
      toast.success("Senha alterada! Entre novamente com a nova senha.");
      await logout();
      navigate("/login");
    } catch (e) {
      toast.error(formatApiError(e));
      setSavingPw(false);
    }
  };

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      const { data } = await api.put("/saas/domain", { domain });
      setUser({ ...user, custom_domain: data.custom_domain });
      toast.success(data.custom_domain ? `Domínio ${data.custom_domain} vinculado` : "Domínio removido");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDomain(false);
    }
  };

  const verifyDomain = async () => {
    setVerifyingDomain(true);
    try {
      const { data } = await api.post("/saas/domain/verify");
      setUser({ ...user, domain_dns_status: data.status, domain_checked_at: data.checked_at });
      if (data.status === "active") {
        toast.success("Domínio ativo — o DNS já aponta para a plataforma");
      } else {
        toast.error(data.detail || "DNS ainda não aponta para a plataforma");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifyingDomain(false);
    }
  };

  const resetMfaFlow = () => { setMfaStep(null); setTotpSetup(null); setMfaCode(""); };

  const saveSession = async () => {
    setSavingSession(true);
    try {
      await api.put("/auth/session", { session_hours: sessionHours });
      setUser({ ...user, session_hours: sessionHours });
      toast.success("Tempo de sessão atualizado — vale a partir do próximo login");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSession(false);
    }
  };

  const startTotp = async () => {
    setMfaBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/totp/setup");
      setTotpSetup(data);
      setMfaStep("totp");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMfaBusy(false);
    }
  };

  const confirmTotp = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/totp/confirm", { code: mfaCode.trim() });
      setUser({ ...user, mfa_enabled: true, mfa_method: "totp" });
      resetMfaFlow();
      toast.success("MFA ativado com Google Authenticator");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMfaBusy(false);
    }
  };

  const startTelegram = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/telegram/start");
      setMfaStep("telegram");
      toast.info("Código enviado no seu Telegram");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMfaBusy(false);
    }
  };

  const confirmTelegram = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/telegram/confirm", { code: mfaCode.trim() });
      setUser({ ...user, mfa_enabled: true, mfa_method: "telegram" });
      resetMfaFlow();
      toast.success("MFA ativado com Telegram");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMfaBusy(false);
    }
  };

  const disableMfa = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: mfaPw });
      setUser({ ...user, mfa_enabled: false, mfa_method: null });
      setMfaPw("");
      toast.success("Verificação em 2 etapas desativada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMfaBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader eyebrow="Conta" title="Ajustes" subtitle="Perfil, segurança e domínio customizado." />
      <TutorialHint
        id="settings"
        title="Sua conta e segurança"
        steps={[
          "Perfil: edite nome e foto — eles aparecem no topo do painel.",
          "Segurança: troque a senha quando quiser (as sessões antigas são encerradas) e ative o MFA com Google Authenticator para proteção extra.",
          "Domínio próprio: aponte seu domínio no Cloudflare (passo a passo na seção) e todos os seus links de playlist/streaming saem com a SUA marca.",
        ]}
      />

      <div className="grid md:grid-cols-2 gap-5 [&>*]:min-w-0">
        <GlassCard className="p-6 anim-fade-up" data-testid="profile-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
              <UserCircle size={20} className="text-cyan-400" />
            </div>
            <h3 className="font-display text-white font-semibold text-sm">Perfil</h3>
          </div>
          <div className="flex items-center gap-4 mb-5">
            <div className="w-16 h-16 rounded-2xl overflow-hidden border border-slate-700 bg-slate-900 flex items-center justify-center shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Foto de perfil" data-testid="profile-avatar-img" className="w-full h-full object-cover" />
              ) : (
                <UserCircle size={30} className="text-slate-600" data-testid="profile-avatar-fallback" />
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
              <button
                data-testid="avatar-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploadingAvatar}
                className="btn-ghost-dark text-white text-xs font-semibold px-4 py-2.5 rounded-xl inline-flex items-center gap-2"
              >
                {uploadingAvatar ? <Spinner /> : <Camera size={14} />} {uploadingAvatar ? "Enviando..." : "Trocar foto"}
              </button>
              <p className="text-[10px] text-slate-500 mt-2">PNG, JPG ou WebP até 2MB</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nome</label>
              <div className="flex flex-wrap gap-3">
                <input
                  data-testid="profile-name-input"
                  className="input-dark flex-1 min-w-0 rounded-xl px-4 py-3 text-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                />
                <button data-testid="profile-name-save-btn" onClick={saveName} disabled={savingName || !name.trim()} className="btn-neon px-5 py-3 rounded-xl text-sm">
                  {savingName ? <Spinner /> : "Salvar"}
                </button>
              </div>
            </div>
            <p className="text-sm text-slate-400" data-testid="profile-email">{user.email}</p>
            <p className="text-[10px] font-mono2 uppercase tracking-widest text-cyan-400/80">{user.role}</p>
          </div>
        </GlassCard>

        <GlassCard className="p-6 anim-fade-up anim-delay-1" data-testid="security-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-rose-400/10 border border-rose-400/25 flex items-center justify-center">
              <KeyRound size={20} className="text-rose-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Segurança</h3>
              <p className="text-[11px] text-slate-500">Ao trocar a senha, todas as sessões são encerradas</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Senha atual</label>
              <input data-testid="current-password-input" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="••••••••" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nova senha</label>
              <input data-testid="new-password-input" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Mínimo 6 caracteres" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Confirmar nova senha</label>
              <input data-testid="confirm-password-input" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Repita a nova senha" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            </div>
            <button
              data-testid="change-password-btn"
              onClick={changePassword}
              disabled={savingPw || !currentPw || !newPw || !confirmPw}
              className="btn-ghost-dark w-full py-3 rounded-xl text-sm text-white font-semibold"
            >
              {savingPw ? <Spinner /> : "Alterar senha"}
            </button>
          </div>
        </GlassCard>

        <GlassCard className="p-6 anim-fade-up anim-delay-2" data-testid="session-mfa-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center">
              <ShieldCheck size={20} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Sessão e verificação em 2 etapas</h3>
              <p className="text-[11px] text-slate-500">Quanto tempo fica com a sessão ativa + proteção extra no login</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Tempo conectado sem precisar entrar de novo</label>
            <div className="flex flex-wrap gap-3">
              <select data-testid="session-hours-select" className="input-dark flex-1 min-w-0 rounded-xl px-4 py-3 text-sm" value={sessionHours} onChange={(e) => setSessionHours(Number(e.target.value))}>
                <option value={1}>1 hora</option>
                <option value={2}>2 horas</option>
                <option value={6}>6 horas</option>
                <option value={12}>12 horas</option>
                <option value={24}>24 horas (padrão)</option>
                <option value={168}>7 dias</option>
                <option value={720}>30 dias</option>
                <option value={0}>Permanente — só sai se clicar em Sair</option>
              </select>
              <button data-testid="session-save-btn" onClick={saveSession} disabled={savingSession} className="btn-neon px-5 py-3 rounded-xl text-sm shrink-0">
                {savingSession ? <Spinner /> : "Salvar"}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">Vale a partir do próximo login.</p>
          </div>

          <div className="border-t border-slate-800/60 pt-5">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400">Verificação em 2 etapas (MFA)</label>
              <span data-testid="mfa-status" className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${user.mfa_enabled ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" : "text-slate-500 border-slate-700 bg-slate-800/40"}`}>
                {user.mfa_enabled ? `Ativo · ${user.mfa_method === "totp" ? "Google Authenticator" : "Telegram"}` : "Desativado"}
              </span>
            </div>

            {!user.mfa_enabled && !mfaStep && (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-500">Ao ativar, todo login vai pedir um código além da senha. Escolha o canal:</p>
                <div className="grid grid-cols-2 gap-3">
                  <button data-testid="mfa-totp-start-btn" onClick={startTotp} disabled={mfaBusy} className="btn-ghost-dark px-4 py-3 rounded-xl text-xs text-white font-semibold">
                    Google Authenticator
                  </button>
                  <button data-testid="mfa-telegram-start-btn" onClick={startTelegram} disabled={mfaBusy} className="btn-ghost-dark px-4 py-3 rounded-xl text-xs text-white font-semibold">
                    Telegram
                  </button>
                </div>
              </div>
            )}

            {mfaStep === "totp" && totpSetup && (
              <div className="space-y-3">
                <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 text-center">
                  <img data-testid="mfa-totp-qr" src={`data:image/png;base64,${totpSetup.qr_png_base64}`} alt="QR Code MFA" className="w-40 h-40 mx-auto rounded-lg" />
                  <p className="text-[10px] text-slate-500 mt-2">Escaneie no Google Authenticator ou digite: <span className="font-mono2 text-cyan-300 select-all">{totpSetup.secret}</span></p>
                </div>
                <input data-testid="mfa-totp-code-input" inputMode="numeric" placeholder="Código de 6 dígitos" maxLength={6} className="input-dark w-full rounded-xl px-4 py-3 text-center text-lg tracking-[0.4em] font-mono2" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
                <div className="flex gap-2">
                  <button data-testid="mfa-totp-confirm-btn" onClick={confirmTotp} disabled={mfaBusy || mfaCode.trim().length < 6} className="btn-neon flex-1 py-3 rounded-xl text-sm">
                    {mfaBusy ? <Spinner /> : "Ativar"}
                  </button>
                  <button data-testid="mfa-cancel-btn" onClick={resetMfaFlow} className="btn-ghost-dark px-4 py-3 rounded-xl text-xs text-slate-400">Cancelar</button>
                </div>
              </div>
            )}

            {mfaStep === "telegram" && (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-400">Enviamos um código de 6 dígitos no seu Telegram. Digite abaixo:</p>
                <input data-testid="mfa-telegram-code-input" inputMode="numeric" placeholder="Código de 6 dígitos" maxLength={6} className="input-dark w-full rounded-xl px-4 py-3 text-center text-lg tracking-[0.4em] font-mono2" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
                <div className="flex gap-2">
                  <button data-testid="mfa-telegram-confirm-btn" onClick={confirmTelegram} disabled={mfaBusy || mfaCode.trim().length < 6} className="btn-neon flex-1 py-3 rounded-xl text-sm">
                    {mfaBusy ? <Spinner /> : "Ativar"}
                  </button>
                  <button data-testid="mfa-cancel-btn" onClick={resetMfaFlow} className="btn-ghost-dark px-4 py-3 rounded-xl text-xs text-slate-400">Cancelar</button>
                </div>
              </div>
            )}

            {user.mfa_enabled && (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-500">Para desativar, confirme sua senha:</p>
                <div className="flex gap-2">
                  <input data-testid="mfa-disable-password-input" type="password" placeholder="Sua senha" className="input-dark flex-1 rounded-xl px-4 py-3 text-sm" value={mfaPw} onChange={(e) => setMfaPw(e.target.value)} />
                  <button data-testid="mfa-disable-btn" onClick={disableMfa} disabled={mfaBusy || !mfaPw} className="btn-ghost-dark px-5 py-3 rounded-xl text-sm text-rose-300 shrink-0">
                    {mfaBusy ? <Spinner /> : "Desativar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </GlassCard>

        <GlassCard className="p-6 anim-fade-up anim-delay-3 md:col-span-2" data-testid="domain-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-purple-400/10 border border-purple-400/25 flex items-center justify-center">
              <Globe size={20} className="text-purple-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Domínio customizado</h3>
              <p className="text-[11px] text-slate-500">Use seu próprio domínio nos links M3U — troque em caso de bloqueio</p>
            </div>
          </div>

          <div className="rounded-xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 mb-5 flex items-start gap-2.5" data-testid="proxy-warning">
            <TriangleAlert size={15} className="text-amber-300 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200 leading-relaxed">
              <b>Obrigatório:</b> na Cloudflare, o proxy precisa estar <b>ativado (nuvem laranja / Proxied)</b>. Com a nuvem cinza (DNS only) os links não funcionam.
            </p>
          </div>

          <ol className="space-y-2.5 mb-6">
            {CLOUDFLARE_STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-xs text-slate-300 leading-relaxed">
                <span className="w-5 h-5 rounded-full bg-cyan-400/10 border border-cyan-400/25 text-cyan-300 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>

          <div className="flex gap-3 max-w-xl">
            <input
              data-testid="custom-domain-input"
              className="input-dark flex-1 rounded-xl px-4 py-3 text-sm font-mono2"
              placeholder="omeudominio.pt"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
            <button data-testid="custom-domain-save-btn" onClick={saveDomain} disabled={savingDomain} className="btn-violet px-5 py-3 rounded-xl text-sm">
              {savingDomain ? <Spinner /> : "Salvar"}
            </button>
          </div>
          {user.custom_domain && (
            <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="domain-status-row">
              {user.domain_dns_status === "active" ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-3 py-1.5" data-testid="domain-active-badge">
                  <ShieldCheck size={13} /> Domínio ativo
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1.5" data-testid="domain-pending-badge">
                  <TriangleAlert size={13} /> DNS pendente — conclua os passos acima e verifique
                </span>
              )}
              <span className="text-xs text-slate-400" data-testid="domain-current">Configurado: <span className="font-mono2 text-emerald-300">{user.custom_domain}</span></span>
              <button data-testid="domain-verify-btn" onClick={verifyDomain} disabled={verifyingDomain} className="btn-ghost-dark px-3.5 py-1.5 rounded-xl text-xs inline-flex items-center gap-1.5 text-slate-300">
                {verifyingDomain ? <Spinner /> : <RefreshCw size={12} />} Verificar agora
              </button>
            </div>
          )}
        </GlassCard>

        <GlassCard data-testid="dedup-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
              <RefreshCw size={20} className="text-amber-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Deduplicação de armazenamento</h3>
              <p className="text-[11px] text-slate-500">Ficheiros idênticos passam a partilhar um único objeto na nuvem — poupa quota Wasabi</p>
            </div>
          </div>
          <div className="text-xs text-slate-400 mb-4 space-y-1.5">
            <p>Sem fingerprint: <b className="text-white font-mono2" data-testid="dedup-nohash">{dedupStatus ? dedupStatus.without_hash : "…"}</b> · Grupos de conteúdo duplicado: <b className="text-white font-mono2" data-testid="dedup-groups">{dedupStatus ? dedupStatus.dup_groups : "…"}</b></p>
            {dedupStatus?.job && (
              <p data-testid="dedup-job-line">Último processo ({dedupStatus.job.kind}): <b className="font-mono2 text-cyan-300">{dedupStatus.job.status}</b> · {dedupStatus.job.done}/{dedupStatus.job.total}{dedupStatus.job.reclaimed_bytes ? ` · libertados ${fmtBytes(dedupStatus.job.reclaimed_bytes)}` : ""}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="dedup-backfill-btn" onClick={() => runDedup("backfill")} disabled={!!dedupBusy} className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2 text-slate-200">
              {dedupBusy === "backfill" ? <Spinner /> : null}1. Calcular fingerprints (antigos/Telegram)
            </button>
            <button data-testid="dedup-reclaim-btn" onClick={() => runDedup("reclaim")} disabled={!!dedupBusy} className="btn-neon px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
              {dedupBusy === "reclaim" ? <Spinner /> : null}2. Reclamar espaço duplicado
            </button>
          </div>
          <p className="text-[10px] text-slate-500 mt-3">Corre o 1 primeiro (uma vez; demora conforme o nº de ficheiros) e depois o 2. As cópias físicas só são apagadas quando nenhuma versão as usa — nada parte.</p>
        </GlassCard>

        {isOwner && (
        <GlassCard>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center">
              <ShieldCheck size={20} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Código completo do painel</h3>
              <p className="text-[11px] text-slate-500">Backend + frontend + memória — pronto para abrir noutro local</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 mb-4">Descarrega o ZIP com todo o código do painel CineBot (sem dependências/build/segredos — instala com <code className="font-mono2 text-cyan-300">pip install -r backend/requirements.txt</code> e <code className="font-mono2 text-cyan-300">yarn install</code>). Gerado na hora e exclusivo do dono.</p>
          <a data-testid="download-painel-zip" href="/api/system/source-zip" download="cinebot-painel.zip"
            className="btn-neon inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm no-underline">
            <RefreshCw size={15} /> Descarregar cinebot-painel.zip
          </a>
        </GlassCard>
        )}
      </div>
    </div>
  );
}
